/**
 * Liquidity & Cash Flow Section
 * Extracted from FinancePage.jsx for better maintainability
 * Design: Quiet Luxury Corporativo - Angolan Night theme
 */

import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, Wallet, DollarSign, ArrowDownUp, Banknote,
  PiggyBank, Building2, RefreshCw, ArrowUpRight, ArrowDownRight, Minus,
  Clock, Landmark, Percent, Activity, FileText, Info, Target, CreditCard, BarChart3
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// Import REAL Cash Flow data
import { 
  realCashFlowData, 
  toMillionsUSD 
} from '../../data/mock/realFinancialData';

// Utility
const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Compact KPI Tooltip - Standard Format
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

// ============ SEMANTIC COLOR SYSTEM - SCORECARD PATTERN ============
const statusColors = {
  positive: '#0C8A4B',
  positiveBg: '#EAF7EF',
  warning: '#A07A18',
  warningBg: '#F8F1DD',
  negative: '#C33D52',
  negativeBg: '#FBECEF',
};

const getSemanticBadgeStyle = (val, inverse = false, useWarning = true) => {
  if (val === undefined || val === null) return { bg: 'bg-slate-100', text: 'text-slate-600' };
  const isFavorable = inverse ? val <= 0 : val >= 0;
  const absVal = Math.abs(val);
  if (useWarning && !isFavorable && absVal <= 5) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', bgColor: statusColors.warningBg, color: statusColors.warning };
  }
  if (isFavorable) {
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', bgColor: statusColors.positiveBg, color: statusColors.positive };
  }
  return { bg: 'bg-rose-100', text: 'text-rose-700', bgColor: statusColors.negativeBg, color: statusColors.negative };
};

const getTrendIconInfo = (vsBudget, vsYoY, inverse = false) => {
  const mainVal = vsBudget !== undefined ? vsBudget : vsYoY;
  if (mainVal === undefined) return null;
  const isFavorable = inverse ? mainVal <= 0 : mainVal >= 0;
  const isUp = mainVal > 0;
  return {
    isUp,
    bgColor: isFavorable ? statusColors.positiveBg : statusColors.negativeBg,
    iconColor: isFavorable ? statusColors.positive : statusColors.negative,
  };
};

// KPI Card - SCORECARD EXECUTIVO PATTERN
const KPICard = ({ 
  title, value, unit, change, changeUnit = '%', isPositive, subtitle, icon: Icon, vsBudget, language = 'pt',
  tooltipData, inverse = false, useWarning = true 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Get semantic styles
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const yoyStyle = getSemanticBadgeStyle(change, inverse, useWarning);
  const trendInfo = getTrendIconInfo(vsBudget, change, inverse);
  
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300 overflow-visible relative`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Title, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Title - Normal case, not uppercase */}
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          
          {/* Value + Unit - Smaller size to match Personnel Costs */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">
              {value}
            </span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          
          {subtitle && <p className="text-xs text-slate-400 mb-2">{subtitle}</p>}
          
          {/* Comparison Badges - Inline compact style with no wrap */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {vsBudget !== undefined && vsBudget !== null && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${budgetStyle.bg} ${budgetStyle.text}`}>
                {parseFloat(vsBudget) >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {parseFloat(vsBudget) > 0 ? '+' : ''}{vsBudget}% Orç.
              </span>
            )}
            {change !== undefined && change !== null && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${yoyStyle.bg} ${yoyStyle.text}`}>
                {parseFloat(change) >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {parseFloat(change) > 0 ? '+' : ''}{change}{changeUnit} YoY
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {trendInfo && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <ArrowUpRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            ) : (
              <ArrowDownRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            )}
          </div>
        )}
      </div>
      
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
    </div>
  );
};

// KPI Card with Tooltip - SCORECARD EXECUTIVO PATTERN
const KPICardWithTooltip = ({ 
  title, value, unit, vsBudget, vsBudgetValue, vsYoY, 
  tooltipData, 
  bgClass = '', language, inverse = false, useWarning = true 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Get semantic styles
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const yoyStyle = getSemanticBadgeStyle(vsYoY, inverse, useWarning);
  const trendInfo = getTrendIconInfo(vsBudget, vsYoY, inverse);
  
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300 overflow-visible relative ${bgClass}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Title, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Title - Normal case, not uppercase */}
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          
          {/* Value + Unit - Smaller size to match Personnel Costs */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">
              {value}
            </span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          
          {/* Comparison Badges - Inline compact style with no wrap */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {vsBudget !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${budgetStyle.bg} ${budgetStyle.text}`}>
                {vsBudget >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsBudget >= 0 ? '+' : ''}{vsBudget}% Orç.
              </span>
            )}
            {vsYoY !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${yoyStyle.bg} ${yoyStyle.text}`}>
                {vsYoY >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsYoY >= 0 ? '+' : ''}{vsYoY}% YoY
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {trendInfo && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <ArrowUpRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            ) : (
              <ArrowDownRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            )}
          </div>
        )}
      </div>
      
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
    </div>
  );
};

const LiquidityCashFlow = ({ year, language, setActiveSection, flowMap }) => {
  const fcfDetails = mockData.freeCashFlowDetails[year];
  const cashFlowData = mockData.cashFlowStatement[year];
  const prevCashFlowData = mockData.cashFlowStatement[year - 1] || mockData.cashFlowStatement[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? 'vs Orç. 1T' : 'vs Orç.';
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';
  
  // ============ REAL CASH FLOW DATA ============
  // For each year: use REAL data from realCashFlowData for the budget column
  const getRealCashFlowData = (yr) => {
    const data = realCashFlowData[yr];
    if (!data) return null;
    // For 2026, use Q1 data (quarterly view)
    return yr === 2026 ? data.Q1 : data;
  };
  
  // Get annual budget data for 2025 (to calculate 25% for Q1 comparison)
  const getAnnualBudgetData = (yr) => {
    const data = realCashFlowData[yr];
    if (!data) return null;
    return data;
  };
  
  const budgetRealData = getRealCashFlowData(year);
  const prevBudgetRealData = getRealCashFlowData(year - 1);
  // For 2026 view, we need 25% of 2025 annual budget for the "1T2025" column
  const annualBudget2025 = year === 2026 ? getAnnualBudgetData(2025) : null;
  
  // Budget Cash Flow values from REAL Excel data - converted to Millions USD
  const budgetCashFlowData = budgetRealData ? {
    customerReceipts: toMillionsUSD(budgetRealData.recebimentosClientes),
    supplierPayments: toMillionsUSD(budgetRealData.pagamentosFornecedores),
    personnelPayments: toMillionsUSD(budgetRealData.pagamentosPessoal),
    cashFromOperations: toMillionsUSD(budgetRealData.caixaGeradaOperacoes),
    taxPayments: toMillionsUSD(budgetRealData.pagamentosEstado),
    otherOperating: toMillionsUSD(budgetRealData.outrosRecebimentosPagamentos),
    operatingCashFlow: toMillionsUSD(budgetRealData.fluxoCaixaOperacional),
    fixedAssetPayments: toMillionsUSD(budgetRealData.fornecedoresAtivosFixos),
    financialInvestments: toMillionsUSD(budgetRealData.investimentosFinanceiros || 0),
    investingCashFlow: toMillionsUSD(budgetRealData.fluxoCaixaInvestimento),
    loansReceived: toMillionsUSD(budgetRealData.financiamentosObtidos),
    interestReceived: toMillionsUSD(budgetRealData.jurosRendimentosSimilares || 0),
    loanRepayments: toMillionsUSD(budgetRealData.reembolsoFinanciamentos),
    interestPaid: toMillionsUSD(budgetRealData.jurosGastosSimilares),
    dividendsPaid: toMillionsUSD(budgetRealData.dividendosPagos || 0),
    financingCashFlow: toMillionsUSD(budgetRealData.fluxoCaixaFinanciamento),
    netCashChange: toMillionsUSD(budgetRealData.variacaoCaixa),
    fxEffect: toMillionsUSD(budgetRealData.efeitoDiferencasCambio),
    openingCash: toMillionsUSD(budgetRealData.caixaInicioPeriodo),
    closingCash: toMillionsUSD(budgetRealData.caixaFinalPeriodo),
  } : null;
  
  // ============ ACTUAL CASH FLOW DATA (Simulated) ============
  // For 2025: Use Budget + 5% to simulate executed values (no real data available)
  // For 2026: Use Q1 Budget + 7% to simulate executed values for Q1
  const actualFactor = year === 2025 ? 1.05 : (year === 2026 ? 1.07 : 1.0);
  
  const actualCashFlowData = budgetCashFlowData ? {
    customerReceipts: budgetCashFlowData.customerReceipts * actualFactor,
    supplierPayments: budgetCashFlowData.supplierPayments * actualFactor,
    personnelPayments: budgetCashFlowData.personnelPayments * actualFactor,
    cashFromOperations: budgetCashFlowData.cashFromOperations * actualFactor,
    taxPayments: budgetCashFlowData.taxPayments * actualFactor,
    otherOperating: budgetCashFlowData.otherOperating * actualFactor,
    operatingCashFlow: budgetCashFlowData.operatingCashFlow * actualFactor,
    fixedAssetPayments: budgetCashFlowData.fixedAssetPayments * actualFactor,
    financialInvestments: budgetCashFlowData.financialInvestments * actualFactor,
    investingCashFlow: budgetCashFlowData.investingCashFlow * actualFactor,
    loansReceived: budgetCashFlowData.loansReceived * actualFactor,
    interestReceived: budgetCashFlowData.interestReceived * actualFactor,
    loanRepayments: budgetCashFlowData.loanRepayments * actualFactor,
    interestPaid: budgetCashFlowData.interestPaid * actualFactor,
    dividendsPaid: budgetCashFlowData.dividendsPaid * actualFactor,
    financingCashFlow: budgetCashFlowData.financingCashFlow * actualFactor,
    netCashChange: budgetCashFlowData.netCashChange * actualFactor,
    fxEffect: budgetCashFlowData.fxEffect * actualFactor,
    openingCash: budgetCashFlowData.openingCash * actualFactor,
    closingCash: budgetCashFlowData.closingCash * actualFactor,
  } : null;
  
  // Previous year REAL data for YoY column
  // For 2025 view: shows 2024 actual data
  // For 2026 view: shows 25% of 2025 annual budget (to simulate Q1 2025)
  const prevYearRealData = year === 2026 && annualBudget2025 ? {
    // For 2026 view, use 25% of 2025 annual budget to represent Q1 2025
    customerReceipts: toMillionsUSD(annualBudget2025.recebimentosClientes) * 0.25,
    supplierPayments: toMillionsUSD(annualBudget2025.pagamentosFornecedores) * 0.25,
    personnelPayments: toMillionsUSD(annualBudget2025.pagamentosPessoal) * 0.25,
    cashFromOperations: toMillionsUSD(annualBudget2025.caixaGeradaOperacoes) * 0.25,
    taxPayments: toMillionsUSD(annualBudget2025.pagamentosEstado) * 0.25,
    otherOperating: toMillionsUSD(annualBudget2025.outrosRecebimentosPagamentos) * 0.25,
    operatingCashFlow: toMillionsUSD(annualBudget2025.fluxoCaixaOperacional) * 0.25,
    fixedAssetPayments: toMillionsUSD(annualBudget2025.fornecedoresAtivosFixos) * 0.25,
    financialInvestments: toMillionsUSD(annualBudget2025.investimentosFinanceiros || 0) * 0.25,
    investingCashFlow: toMillionsUSD(annualBudget2025.fluxoCaixaInvestimento) * 0.25,
    loansReceived: toMillionsUSD(annualBudget2025.financiamentosObtidos) * 0.25,
    interestReceived: toMillionsUSD(annualBudget2025.jurosRendimentosSimilares || 0) * 0.25,
    loanRepayments: toMillionsUSD(annualBudget2025.reembolsoFinanciamentos) * 0.25,
    interestPaid: toMillionsUSD(annualBudget2025.jurosGastosSimilares) * 0.25,
    dividendsPaid: toMillionsUSD(annualBudget2025.dividendosPagos || 0) * 0.25,
    financingCashFlow: toMillionsUSD(annualBudget2025.fluxoCaixaFinanciamento) * 0.25,
    netCashChange: toMillionsUSD(annualBudget2025.variacaoCaixa) * 0.25,
    fxEffect: toMillionsUSD(annualBudget2025.efeitoDiferencasCambio) * 0.25,
    openingCash: toMillionsUSD(annualBudget2025.caixaInicioPeriodo) * 0.25,
    closingCash: toMillionsUSD(annualBudget2025.caixaFinalPeriodo) * 0.25,
  } : prevBudgetRealData ? {
    customerReceipts: toMillionsUSD(prevBudgetRealData.recebimentosClientes),
    supplierPayments: toMillionsUSD(prevBudgetRealData.pagamentosFornecedores),
    personnelPayments: toMillionsUSD(prevBudgetRealData.pagamentosPessoal),
    cashFromOperations: toMillionsUSD(prevBudgetRealData.caixaGeradaOperacoes),
    taxPayments: toMillionsUSD(prevBudgetRealData.pagamentosEstado),
    otherOperating: toMillionsUSD(prevBudgetRealData.outrosRecebimentosPagamentos),
    operatingCashFlow: toMillionsUSD(prevBudgetRealData.fluxoCaixaOperacional),
    fixedAssetPayments: toMillionsUSD(prevBudgetRealData.fornecedoresAtivosFixos),
    financialInvestments: toMillionsUSD(prevBudgetRealData.investimentosFinanceiros || 0),
    investingCashFlow: toMillionsUSD(prevBudgetRealData.fluxoCaixaInvestimento),
    loansReceived: toMillionsUSD(prevBudgetRealData.financiamentosObtidos),
    interestReceived: toMillionsUSD(prevBudgetRealData.jurosRendimentosSimilares || 0),
    loanRepayments: toMillionsUSD(prevBudgetRealData.reembolsoFinanciamentos),
    interestPaid: toMillionsUSD(prevBudgetRealData.jurosGastosSimilares),
    dividendsPaid: toMillionsUSD(prevBudgetRealData.dividendosPagos || 0),
    financingCashFlow: toMillionsUSD(prevBudgetRealData.fluxoCaixaFinanciamento),
    netCashChange: toMillionsUSD(prevBudgetRealData.variacaoCaixa),
    fxEffect: toMillionsUSD(prevBudgetRealData.efeitoDiferencasCambio),
    openingCash: toMillionsUSD(prevBudgetRealData.caixaInicioPeriodo),
    closingCash: toMillionsUSD(prevBudgetRealData.caixaFinalPeriodo),
  } : null;
  
  // Budget values for cash flow (derived from FCF budget)
  const budgetFCF = budgetData.freeCashFlow?.budget || 42;
  const fcfVsBudget = budgetFCF ? (((fcfDetails?.fcf || 45) - budgetFCF) / budgetFCF * 100).toFixed(0) : 0;
  const fcfVsBudgetAbs = (fcfDetails?.fcf || 45) - budgetFCF;
  
  // Insights
  const liquidityInsights = {
    pt: [
      { title: 'GERAÇÃO DE CAIXA', description: 'O Fluxo de Caixa Operacional robusto reflecte a capacidade de conversão de EBITDA em caixa efectivo.', color: 'text-emerald-400' },
      { title: 'INVESTIMENTO ESTRATÉGICO', description: 'O CAPEX direccionado para capacidade produtiva consome caixa mas sustenta a base de activos a médio prazo.', color: 'text-gold-400' },
      { title: 'POSIÇÃO DE LIQUIDEZ', description: 'A posição final de caixa permite fazer face a compromissos de curto prazo com margem de segurança.', color: 'text-blue-400' },
    ],
    en: [
      { title: 'CASH GENERATION', description: 'Robust Operating Cash Flow reflects the ability to convert EBITDA into actual cash.', color: 'text-emerald-400' },
      { title: 'STRATEGIC INVESTMENT', description: 'CAPEX directed towards productive capacity consumes cash but sustains the asset base in the medium term.', color: 'text-gold-400' },
      { title: 'LIQUIDITY POSITION', description: 'The ending cash position allows meeting short-term commitments with a safety margin.', color: 'text-blue-400' },
    ],
  };

  // Calculate cumulative values for waterfall - USE actualCashFlowData for 2025
  const waterfallData = actualCashFlowData || cashFlowData;
  let cumulative = waterfallData.openingCash;
  const cashWaterfallChartData = [
    { 
      name: language === 'pt' ? 'Caixa Inicial' : 'Opening Cash', 
      value: waterfallData.openingCash, 
      start: 0, 
      barHeight: waterfallData.openingCash,
      isTotal: true 
    },
    { 
      name: language === 'pt' ? 'Caixa Operacional' : 'Operating Cash', 
      value: waterfallData.operatingCashFlow,
      start: cumulative,
      barHeight: waterfallData.operatingCashFlow,
      isPositive: true
    },
    { 
      name: language === 'pt' ? 'Caixa Investimento' : 'Investing Cash', 
      value: waterfallData.investingCashFlow,
      start: (cumulative += waterfallData.operatingCashFlow) + waterfallData.investingCashFlow,
      barHeight: Math.abs(waterfallData.investingCashFlow),
      isPositive: false
    },
    { 
      name: language === 'pt' ? 'Caixa Financiamento' : 'Financing Cash', 
      value: waterfallData.financingCashFlow,
      start: (cumulative += waterfallData.investingCashFlow) + waterfallData.financingCashFlow,
      barHeight: Math.abs(waterfallData.financingCashFlow),
      isPositive: false
    },
    { 
      name: language === 'pt' ? 'Efeito Câmbio' : 'FX Effect', 
      value: waterfallData.fxEffect,
      start: (cumulative += waterfallData.financingCashFlow) + waterfallData.fxEffect,
      barHeight: Math.abs(waterfallData.fxEffect),
      isPositive: waterfallData.fxEffect >= 0
    },
    { 
      name: language === 'pt' ? 'Caixa Final' : 'Closing Cash', 
      value: waterfallData.closingCash, 
      start: 0, 
      barHeight: waterfallData.closingCash,
      isTotal: true 
    },
  ];

  // Cash Flow Statement rows
  const cashFlowRows = [
    { type: 'header', label: { pt: 'Fluxos de Caixa das Atividades Operacionais', en: 'Cash Flows from Operating Activities' } },
    { type: 'row', label: { pt: 'Recebimentos de Clientes', en: 'Customer Receipts' }, key: 'customerReceipts' },
    { type: 'row', label: { pt: 'Pagamentos a Fornecedores', en: 'Supplier Payments' }, key: 'supplierPayments' },
    { type: 'row', label: { pt: 'Pagamentos ao Pessoal', en: 'Personnel Payments' }, key: 'personnelPayments' },
    { type: 'subtotal', label: { pt: 'Caixa Gerado pelas Operações', en: 'Cash from Operations' }, key: 'cashFromOperations' },
    { type: 'row', label: { pt: 'Pagamentos ao Estado', en: 'Tax Payments' }, key: 'taxPayments' },
    { type: 'row', label: { pt: 'Outros Recebimentos/Pagamentos', en: 'Other Operating' }, key: 'otherOperating' },
    { type: 'total', label: { pt: 'Fluxo de Caixa Operacional (1)', en: 'Operating Cash Flow (1)' }, key: 'operatingCashFlow' },
    { type: 'header', label: { pt: 'Fluxos de Caixa das Atividades de Investimento', en: 'Cash Flows from Investing Activities' } },
    { type: 'row', label: { pt: 'Fornecedores de Ativos Fixos', en: 'Fixed Asset Payments' }, key: 'fixedAssetPayments' },
    { type: 'row', label: { pt: 'Investimentos Financeiros', en: 'Financial Investments' }, key: 'financialInvestments' },
    { type: 'total', label: { pt: 'Fluxo de Caixa de Investimento (2)', en: 'Investing Cash Flow (2)' }, key: 'investingCashFlow' },
    { type: 'header', label: { pt: 'Fluxos de Caixa das Atividades de Financiamento', en: 'Cash Flows from Financing Activities' } },
    { type: 'row', label: { pt: 'Financiamentos Obtidos', en: 'Loans Received' }, key: 'loansReceived' },
    { type: 'row', label: { pt: 'Juros e Rendimentos Similares', en: 'Interest Received' }, key: 'interestReceived' },
    { type: 'row', label: { pt: 'Reembolso de Financiamentos', en: 'Loan Repayments' }, key: 'loanRepayments' },
    { type: 'row', label: { pt: 'Juros e Gastos Similares', en: 'Interest Paid' }, key: 'interestPaid' },
    { type: 'row', label: { pt: 'Dividendos Pagos', en: 'Dividends Paid' }, key: 'dividendsPaid' },
    { type: 'total', label: { pt: 'Fluxo de Caixa de Financiamento (3)', en: 'Financing Cash Flow (3)' }, key: 'financingCashFlow' },
    { type: 'header', label: { pt: 'Resumo', en: 'Summary' } },
    { type: 'row', label: { pt: 'Variação de Caixa (1+2+3)', en: 'Net Cash Change (1+2+3)' }, key: 'netCashChange' },
    { type: 'row', label: { pt: 'Efeito das Diferenças de Câmbio', en: 'FX Effect' }, key: 'fxEffect' },
    { type: 'row', label: { pt: 'Caixa no Início do Período', en: 'Opening Cash' }, key: 'openingCash' },
    { type: 'grandtotal', label: { pt: 'Caixa no Final do Período', en: 'Closing Cash' }, key: 'closingCash' },
  ];

  // Calculate YoY changes - using actualCashFlowData for current year
  const actualOCF = actualCashFlowData?.operatingCashFlow || cashFlowData.operatingCashFlow;
  const actualICF = actualCashFlowData?.investingCashFlow || cashFlowData.investingCashFlow;
  const actualFinCF = actualCashFlowData?.financingCashFlow || cashFlowData.financingCashFlow;
  const actualClosingCash = actualCashFlowData?.closingCash || cashFlowData.closingCash;
  
  const ocfChange = prevYearRealData ? ((actualOCF / prevYearRealData.operatingCashFlow - 1) * 100).toFixed(0) : 0;
  const cashChange = prevYearRealData ? ((actualClosingCash / prevYearRealData.closingCash - 1) * 100).toFixed(0) : 0;
  
  // Budget values for all cash flow items - USE REAL DATA (unchanged budget)
  const budgetOCF = budgetCashFlowData?.operatingCashFlow || actualOCF * 0.95;
  const budgetICF = budgetCashFlowData?.investingCashFlow || actualICF * 1.05;
  const budgetFinCF = budgetCashFlowData?.financingCashFlow || actualFinCF * 0.92;
  const budgetClosingCash = budgetCashFlowData?.closingCash || (cashFlowData.openingCash + budgetFCF - 5);
  
  // vs Budget calculations - compare actualCashFlowData vs budgetCashFlowData
  const ocfVsBudget = budgetOCF !== 0 ? ((actualOCF - budgetOCF) / Math.abs(budgetOCF) * 100).toFixed(0) : '0';
  const icfVsBudget = budgetICF !== 0 ? ((actualICF - budgetICF) / Math.abs(budgetICF) * 100).toFixed(0) : '0';
  const finCFVsBudget = budgetFinCF !== 0 ? ((actualFinCF - budgetFinCF) / Math.abs(budgetFinCF) * 100).toFixed(0) : '0';
  const cashVsBudget = budgetClosingCash !== 0 ? ((actualClosingCash - budgetClosingCash) / Math.abs(budgetClosingCash) * 100).toFixed(0) : '0';
  
  // YoY for investing and financing - using actualCashFlowData
  const icfYoY = prevYearRealData ? ((actualICF - prevYearRealData.investingCashFlow) / Math.abs(prevYearRealData.investingCashFlow) * 100).toFixed(0) : 0;
  const finCFYoY = prevYearRealData ? ((actualFinCF - prevYearRealData.financingCashFlow) / Math.abs(prevYearRealData.financingCashFlow) * 100).toFixed(0) : 0;

  return (
    <div className="space-y-8" data-testid="liquidity-cashflow-section">
      {/* Page Title */}
      <div>
        <h1 
          className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
          style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
        >
          {language === 'pt' ? 'Liquidez e Geração de Caixa' : 'Liquidity & Cash Generation'} | {titleSuffix}
        </h1>
        <p 
          className="text-[15px] leading-[22px]"
          style={{ color: '#425070' }}
        >
          {comparisonSubtitle}
        </p>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {liquidityInsights[language].map((insight, idx) => (
            <div 
              key={idx} 
              className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  idx === 0 ? 'bg-emerald-500/30' : idx === 1 ? 'bg-amber-500/30' : 'bg-blue-500/30'
                }`}>
                  {idx === 0 ? (
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  ) : idx === 1 ? (
                    <Building2 className="w-5 h-5 text-amber-400" />
                  ) : (
                    <Wallet className="w-5 h-5 text-blue-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KPIs - Single Row with 6 KPIs - Same style as CMVMC */}
      <div className="grid grid-cols-6 gap-4">
        <KPICard
          title={language === 'pt' ? 'Conversão EBITDA → Caixa' : 'EBITDA → Cash Conversion'}
          value="74"
          unit="%"
          vsBudget={5}
          change={8}
          isPositive={true}
          icon={Percent}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Conversão EBITDA → Caixa Operacional' : 'EBITDA to Operating Cash Conversion',
            definition: language === 'pt' 
            ? 'Percentagem do EBITDA convertido em caixa operacional efectivo. Mede a qualidade dos resultados operacionais e a eficiência na gestão do capital circulante.'
            : 'Percentage of EBITDA converted into actual operating cash. Measures operating results quality and working capital management efficiency.',
            relevance: 'Indicador relevante para decisões de gestão.',
            currentReading: language === 'pt' 
            ? 'Taxa de 74% indica elevada qualidade dos lucros. A empresa retém mais caixa das operações, reforçando capacidade de investimento e serviço de dívida.'
            : '74% rate indicates high earnings quality. Company retains more cash from operations, strengthening investment and debt service capacity.'
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Runway de Liquidez' : 'Liquidity Runway'}
          value="8.0"
          unit={language === 'pt' ? 'meses' : 'months'}
          vsBudget={12}
          change={15}
          isPositive={true}
          icon={Clock}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Runway de Liquidez' : 'Liquidity Runway',
            definition: language === 'pt' 
            ? 'Número de meses que a empresa consegue operar com a caixa disponível actual, mantendo o nível de despesas operacionais correntes.'
            : 'Number of months the company can operate with current available cash, maintaining current operating expense levels.',
            relevance: 'Indicador relevante para decisões de gestão.',
            currentReading: language === 'pt' 
            ? 'Runway de 8 meses proporciona margem confortável para compromissos de curto prazo e flexibilidade para decisões estratégicas sem pressão imediata de liquidez.'
            : '8-month runway provides comfortable margin for short-term commitments and flexibility for strategic decisions without immediate liquidity pressure.'
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Fluxo de Caixa Livre' : 'Free Cash Flow'}
          value={formatM(fcfDetails?.fcf || 45)}
          unit="M USD"
          vsBudget={parseInt(fcfVsBudget)}
          change={61}
          isPositive={true}
          icon={Banknote}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Fluxo de Caixa Livre (FCL)' : 'Free Cash Flow (FCF)',
            definition: language === 'pt' 
            ? 'Caixa disponível após financiar operações e investimentos de manutenção. Representa o caixa que pode ser usado para dividendos, redução de dívida ou novos investimentos.'
            : 'Cash available after financing operations and maintenance investments. Represents cash that can be used for dividends, debt reduction, or new investments.',
            relevance: 'Indicador relevante para decisões de gestão.',
            currentReading: language === 'pt' 
            ? 'FCL de USD 45M (+61% YoY) demonstra forte capacidade de geração. Permite distribuição de dividendos, desalavancagem ou financiamento de crescimento orgânico.'
            : 'FCF of USD 45M (+61% YoY) demonstrates strong generation capacity. Enables dividend distribution, deleveraging, or organic growth financing.'
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Caixa Disponível' : 'Available Cash'}
          value={formatM(actualClosingCash)}
          unit="M USD"
          vsBudget={parseInt(cashVsBudget)}
          change={parseInt(cashChange)}
          isPositive={parseInt(cashChange) > 0}
          icon={Wallet}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Caixa Disponível' : 'Available Cash',
            definition: language === 'pt' 
            ? 'Caixa e equivalentes disponíveis no final do período. Representa a liquidez imediata para fazer face a compromissos operacionais e financeiros.'
            : 'Cash and equivalents available at period end. Represents immediate liquidity to meet operating and financial commitments.',
            relevance: 'Indicador relevante para decisões de gestão.',
            currentReading: language === 'pt' 
            ? 'Posição de USD 56M garante cobertura de obrigações de curto prazo e folga para oportunidades de investimento ou contingências operacionais.'
            : 'Position of USD 56M ensures short-term obligation coverage and headroom for investment opportunities or operational contingencies.'
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Moeda Dominante' : 'Dominant Currency'}
          value="USD"
          unit=""
          vsBudget={0}
          change={0}
          isPositive={true}
          icon={DollarSign}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Moeda Dominante da Caixa' : 'Dominant Cash Currency',
            definition: language === 'pt' 
            ? 'Moeda principal em que a empresa mantém a sua posição de caixa. O USD representa 76% da liquidez total, seguido de AOA, EUR e ZAR.'
            : 'Main currency in which the company holds its cash position. USD represents 76% of total liquidity, followed by AOA, EUR, and ZAR.',
            relevance: 'Indicador relevante para decisões de gestão.',
            currentReading: language === 'pt' 
            ? 'Concentração em USD proporciona estabilidade cambial e facilita transações internacionais no mercado de diamantes, reduzindo exposição à volatilidade do AOA.'
            : 'USD concentration provides FX stability and facilitates international transactions in the diamond market, reducing AOA volatility exposure.'
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Concentração Bancária' : 'Bank Concentration'}
          value="BFA"
          unit="52%"
          vsBudget={-3}
          change={-5}
          isPositive={true}
          icon={Landmark}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Concentração Bancária' : 'Bank Concentration',
            definition: language === 'pt' 
            ? 'Percentagem da caixa disponível no maior banco parceiro. BFA detém 52% da posição total, seguido por BAI (22%), BIC (15%) e Standard Bank (11%).'
            : 'Percentage of available cash at largest partner bank. BFA holds 52% of total position, followed by BAI (22%), BIC (15%), and Standard Bank (11%).',
            relevance: 'Indicador relevante para decisões de gestão.',
            currentReading: language === 'pt' 
            ? 'Redução de 5% na concentração face a 2024 indica diversificação gradual do risco de contraparte, embora exposição ao BFA ainda seja material e requeira monitorização.'
            : '5% concentration reduction vs 2024 indicates gradual counterparty risk diversification, although BFA exposure remains material and requires monitoring.'
          }}
        />
      </div>

      {/* Executive Summary - Modular Layout Premium */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Resumo Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Liquidez e geração de caixa' : 'Liquidity and cash generation'}
        language={language}
        maxHeight="550px"
        icon={Info}
        dataTestId="liquidity-executive-summary"
      >
        <div className="space-y-6">
          {/* 1. Visão do Ano */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-slate-600"></div>
              {language === 'pt' ? 'Visão do Ano' : 'Year Overview'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, a secção de <strong>Liquidez e Geração de Caixa</strong> mostra uma empresa com <strong>forte geração operacional</strong>, <strong>fluxo de caixa livre positivo</strong> e <strong>posição de liquidez ainda confortável</strong>, apesar de um perfil de saídas relevante em investimento, financiamento e câmbios. O <strong>fluxo de caixa operacional</strong> fechou em <strong>USD 79,0M</strong>, acima do <strong>orçamento em 5,3%</strong> e <strong>36,2% acima de 2024</strong>. O <strong>fluxo de caixa livre</strong> atingiu <strong>USD 45,0M</strong>, ficando <strong>7% acima do plano</strong> e <strong>61% acima do período homólogo</strong>, enquanto a <strong>conversão de EBITDA em caixa</strong> subiu para <strong>74%</strong>, também acima do orçamento e de 2024.</>
                  : <>In 2025, the <strong>Liquidity and Cash Generation</strong> section shows a company with <strong>strong operating generation</strong>, <strong>positive free cash flow</strong> and <strong>still comfortable liquidity position</strong>, despite a relevant outflow profile in investment, financing and FX. <strong>Operating cash flow</strong> closed at <strong>USD 79.0M</strong>, <strong>5.3% above budget</strong> and <strong>36.2% above 2024</strong>. <strong>Free cash flow</strong> reached <strong>USD 45.0M</strong>, <strong>7% above plan</strong> and <strong>61% above prior year</strong>, while <strong>EBITDA to cash conversion</strong> rose to <strong>74%</strong>.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>A leitura principal para a Gerência é que 2025 confirmou uma capacidade real de <strong>transformar EBITDA em caixa</strong>, mesmo num ano com <strong>CAPEX material</strong>, <strong>saídas financeiras elevadas</strong> e <strong>efeito cambial adverso</strong>. O ponto de atenção não esteve na geração operacional, mas na forma como essa geração foi absorvida abaixo da linha operacional.</>
                  : <>The main reading for Management is that 2025 confirmed a real ability to <strong>transform EBITDA into cash</strong>, even in a year with <strong>material CAPEX</strong>, <strong>high financial outflows</strong> and <strong>adverse FX effect</strong>. The attention point was not in operating generation, but in how that generation was absorbed below the operating line.</>}
              </p>
            </div>
          </div>

          {/* 2. Geração Operacional de Caixa */}
          <div className="bg-emerald-50/60 rounded-xl p-5 border border-emerald-200">
            <h4 className="font-semibold text-sm text-emerald-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
              {language === 'pt' ? 'Geração Operacional de Caixa' : 'Operating Cash Generation'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A base do desempenho do ano esteve na actividade operacional. Os <strong>recebimentos de clientes</strong> totalizaram <strong>USD 292,0M</strong>, acima do orçamento (<strong>+2,0%</strong>) e <strong>22,7% acima de 2024</strong>, sustentando uma <strong>caixa gerada pelas operações de USD 111,0M</strong>. Esta geração foi parcialmente absorvida por:</>
                  : <>The year's performance base was in operating activity. <strong>Customer receipts</strong> totaled <strong>USD 292.0M</strong>, above budget (<strong>+2.0%</strong>) and <strong>22.7% above 2024</strong>, sustaining <strong>cash generated from operations of USD 111.0M</strong>. This generation was partially absorbed by:</>}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li className="flex justify-between"><span>{language === 'pt' ? 'Pagamentos a fornecedores:' : 'Supplier payments:'}</span><span className="font-semibold text-red-600">-USD 118,0M</span></li>
                    <li className="flex justify-between"><span>{language === 'pt' ? 'Pagamentos ao pessoal:' : 'Personnel payments:'}</span><span className="font-semibold text-red-600">-USD 63,0M</span></li>
                  </ul>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li className="flex justify-between"><span>{language === 'pt' ? 'Pagamentos ao Estado:' : 'Tax payments:'}</span><span className="font-semibold text-red-600">-USD 18,0M</span></li>
                    <li className="flex justify-between"><span>{language === 'pt' ? 'Outros receb./pagam.:' : 'Other receipts/payments:'}</span><span className="font-semibold text-red-600">-USD 14,0M</span></li>
                  </ul>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>Mesmo com maior pressão em fornecedores, pessoal e Estado face a 2024, a operação gerou caixa suficiente para fechar o ano com <strong>USD 79,0M de fluxo operacional</strong>, acima do orçamento e materialmente acima do período homólogo. Em termos executivos, isto confirma que o reforço de escala e execução do negócio teve tradução efectiva em liquidez.</>
                  : <>Even with greater pressure on suppliers, personnel and State vs 2024, operations generated enough cash to close the year with <strong>USD 79.0M operating flow</strong>, above budget and materially above prior year.</>}
              </p>
            </div>
          </div>

          {/* 3. Investimento, Free Cash Flow e Absorção */}
          <div className="bg-blue-50/60 rounded-xl p-5 border border-blue-200">
            <h4 className="font-semibold text-sm text-blue-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              {language === 'pt' ? 'Investimento, Free Cash Flow e Absorção' : 'Investment, Free Cash Flow and Absorption'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A principal utilização da caixa gerada foi o investimento. O <strong>fluxo de caixa de investimento</strong> foi de <strong>-USD 40,0M</strong>, composto por:</>
                  : <>The main use of generated cash was investment. <strong>Investing cash flow</strong> was <strong>-USD 40.0M</strong>, composed of:</>}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="bg-white/60 rounded-lg p-3 border border-blue-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Fornec. Activos Fixos' : 'Fixed Asset Suppliers'}</p>
                  <p className="text-lg font-bold text-red-600">-USD 38,0M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-blue-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Invest. Financeiros' : 'Financial Investments'}</p>
                  <p className="text-lg font-bold text-red-600">-USD 2,0M</p>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>Face ao orçamento, a saída de investimento foi <strong>melhor em USD 4,0M</strong> (<strong>+9,1%</strong>), mas ainda acima do nível de 2024 (<strong>-USD 37,0M</strong>). Apesar disso, a geração operacional foi suficientemente forte para suportar este CAPEX e ainda preservar <strong>free cash flow de USD 45,0M</strong>.</>
                  : <>Compared to budget, investment outflow was <strong>better by USD 4.0M</strong> (<strong>+9.1%</strong>), but still above 2024 level (<strong>-USD 37.0M</strong>). Despite this, operating generation was strong enough to support this CAPEX and still preserve <strong>free cash flow of USD 45.0M</strong>.</>}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-blue-100 mt-2">
                <p className="text-xs text-blue-700 font-medium">
                  <span className="text-blue-500">▸ </span>
                  {language === 'pt' 
                    ? 'A implicação de gestão é relevante: o investimento continuou a consumir uma parcela importante da liquidez, mas já não impediu a empresa de encerrar o ano com geração líquida positiva.'
                    : 'The management implication is relevant: investment continued to consume an important portion of liquidity, but no longer prevented the company from closing the year with positive net generation.'}
                </p>
              </div>
            </div>
          </div>

          {/* 4. Financiamento, Dividendos e Efeito Cambial */}
          <div className="bg-violet-50/60 rounded-xl p-5 border border-violet-200">
            <h4 className="font-semibold text-sm text-violet-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-violet-600"></div>
              {language === 'pt' ? 'Financiamento, Dividendos e Efeito Cambial' : 'Financing, Dividends and FX Effect'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A secção mostra que a principal pressão adicional abaixo do free cash flow veio das actividades de financiamento. O <strong>fluxo de caixa de financiamento</strong> fechou em <strong>-USD 21,0M</strong>, significativamente mais negativo do que em 2024 (<strong>-USD 4,0M</strong>) e abaixo do orçamento em <strong>USD 2,1M</strong>. A composição foi a seguinte:</>
                  : <>The section shows that the main additional pressure below free cash flow came from financing activities. <strong>Financing cash flow</strong> closed at <strong>-USD 21.0M</strong>, significantly more negative than in 2024 (<strong>-USD 4.0M</strong>) and below budget by <strong>USD 2.1M</strong>. The composition was:</>}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-violet-100">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-600">{language === 'pt' ? 'Financiamentos obtidos:' : 'Financing obtained:'}</span><span className="font-semibold text-emerald-600">+USD 8,0M</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">{language === 'pt' ? 'Juros e rend. similares:' : 'Interest and similar income:'}</span><span className="font-semibold text-emerald-600">+USD 2,0M</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">{language === 'pt' ? 'Reembolso financiamentos:' : 'Financing repayments:'}</span><span className="font-semibold text-red-600">-USD 15,0M</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">{language === 'pt' ? 'Juros e gastos similares:' : 'Interest and similar expenses:'}</span><span className="font-semibold text-red-600">-USD 6,0M</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">{language === 'pt' ? 'Dividendos pagos:' : 'Dividends paid:'}</span><span className="font-semibold text-red-600">-USD 10,0M</span></div>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>Ou seja, a geração operacional do ano não foi apenas usada para investimento; foi também absorvida por amortizações, encargos financeiros e distribuição de dividendos. A este efeito juntou-se ainda <strong>-USD 4,0M</strong> de impacto cambial, muito acima de <strong>-USD 1,0M em 2024</strong>.</>
                  : <>That is, the year's operating generation was not only used for investment; it was also absorbed by amortizations, financial charges and dividend distribution. To this effect was added <strong>-USD 4.0M</strong> of FX impact, well above <strong>-USD 1.0M in 2024</strong>.</>}
              </p>
              <p className="text-slate-600 italic text-xs">
                {language === 'pt' 
                  ? 'A leitura correcta é que a caixa do ano foi pressionada mais por decisões de alocação e eventos financeiros do que por fragilidade operacional.'
                  : 'The correct reading is that the year\'s cash was pressured more by allocation decisions and financial events than by operational weakness.'}
              </p>
            </div>
          </div>

          {/* 5. Posição Final de Caixa e Runway */}
          <div className="bg-teal-50/60 rounded-xl p-5 border border-teal-200">
            <h4 className="font-semibold text-sm text-teal-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-teal-600"></div>
              {language === 'pt' ? 'Posição Final de Caixa e Runway de Liquidez' : 'Ending Cash Position and Liquidity Runway'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>O ano começou com <strong>USD 42,0M de caixa</strong> e terminou com <strong>USD 56,0M</strong>, após:</>
                  : <>The year started with <strong>USD 42.0M cash</strong> and ended with <strong>USD 56.0M</strong>, after:</>}
              </p>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <div className="bg-white/60 rounded-lg p-2.5 border border-teal-100 text-center">
                  <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Fluxo Operac.' : 'Operating CF'}</p>
                  <p className="text-sm font-bold text-emerald-600">+USD 79,0M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-teal-100 text-center">
                  <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Investimento' : 'Investing'}</p>
                  <p className="text-sm font-bold text-red-600">-USD 40,0M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-teal-100 text-center">
                  <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Financiamento' : 'Financing'}</p>
                  <p className="text-sm font-bold text-red-600">-USD 21,0M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-teal-100 text-center">
                  <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Efeito Cambial' : 'FX Effect'}</p>
                  <p className="text-sm font-bold text-red-600">-USD 4,0M</p>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>No painel, a <strong>caixa disponível</strong> de <strong>USD 56,0M</strong> aparece <strong>33% acima de 2024</strong>, embora abaixo do orçamento da secção. Ainda assim, o <strong>runway de liquidez</strong> fechou em <strong>8,0 meses</strong>, <strong>12% acima do plano</strong> e <strong>15% acima do período homólogo</strong>, o que indica que a posição de curto prazo permaneceu sólida.</>
                  : <>On the panel, <strong>available cash</strong> of <strong>USD 56.0M</strong> appears <strong>33% above 2024</strong>, although below section budget. Still, <strong>liquidity runway</strong> closed at <strong>8.0 months</strong>, <strong>12% above plan</strong> and <strong>15% above prior year</strong>, indicating short-term position remained solid.</>}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-teal-100 mt-2">
                <p className="text-xs text-teal-700 font-medium">
                  <span className="text-teal-500">▸ </span>
                  {language === 'pt' 
                    ? 'Em termos de gestão, isto significa que a empresa terminou o ano com uma almofada de liquidez operacionalmente confortável, mesmo depois de absorver investimento, financiamento e câmbio.'
                    : 'In management terms, this means the company ended the year with an operationally comfortable liquidity cushion, even after absorbing investment, financing and FX.'}
                </p>
              </div>
            </div>
          </div>

          {/* 6. Estrutura da Liquidez */}
          <div className="bg-cyan-50/60 rounded-xl p-5 border border-cyan-200">
            <h4 className="font-semibold text-sm text-cyan-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-cyan-600"></div>
              {language === 'pt' ? 'Estrutura da Liquidez: Disponibilidade, Moeda e Concentração' : 'Liquidity Structure: Availability, Currency and Concentration'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A estrutura da liquidez mostra um equilíbrio razoável entre disponibilidade imediata e remuneração dos excedentes. A caixa total repartiu-se entre:</>
                  : <>The liquidity structure shows a reasonable balance between immediate availability and surplus remuneration. Total cash was split between:</>}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="bg-white/60 rounded-lg p-3 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Depósitos à Ordem' : 'Demand Deposits'}</p>
                  <p className="text-lg font-bold text-cyan-700">USD 38,5M</p>
                  <p className="text-[10px] text-cyan-600 font-semibold">69% {language === 'pt' ? 'da caixa total' : 'of total cash'}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Depósitos a Prazo' : 'Term Deposits'}</p>
                  <p className="text-lg font-bold text-cyan-700">USD 17,5M</p>
                  <p className="text-[10px] text-cyan-600 font-semibold">31% {language === 'pt' ? 'da caixa total' : 'of total cash'}</p>
                </div>
              </div>
              <p className="font-medium text-slate-800 mt-3">
                {language === 'pt' ? 'Do ponto de vista cambial, a posição continuou fortemente concentrada em USD:' : 'From an FX perspective, the position remained heavily concentrated in USD:'}
              </p>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-white/60 rounded-lg p-2 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500">USD</p>
                  <p className="text-sm font-bold text-slate-700">USD 42,5M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500">AOA</p>
                  <p className="text-sm font-bold text-slate-700">USD 8,2M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500">EUR</p>
                  <p className="text-sm font-bold text-slate-700">USD 3,8M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500">ZAR</p>
                  <p className="text-sm font-bold text-slate-700">USD 1,5M</p>
                </div>
              </div>
              <p className="font-medium text-slate-800 mt-3">
                {language === 'pt' ? 'Em termos bancários, a maior exposição continuou no BFA:' : 'In banking terms, the largest exposure remained at BFA:'}
              </p>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-amber-50/50 rounded-lg p-2 border border-amber-100 text-center">
                  <p className="text-xs text-slate-500">BFA</p>
                  <p className="text-sm font-bold text-amber-700">USD 29,1M</p>
                  <p className="text-[10px] text-amber-600 font-semibold">52%</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500">BAI</p>
                  <p className="text-sm font-bold text-slate-700">USD 12,3M</p>
                  <p className="text-[10px] text-slate-500">22%</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500">BIC</p>
                  <p className="text-sm font-bold text-slate-700">USD 8,4M</p>
                  <p className="text-[10px] text-slate-500">15%</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2 border border-cyan-100 text-center">
                  <p className="text-xs text-slate-500">Std Bank</p>
                  <p className="text-sm font-bold text-slate-700">USD 6,2M</p>
                  <p className="text-[10px] text-slate-500">11%</p>
                </div>
              </div>
              <p className="text-slate-600 italic text-xs mt-2">
                {language === 'pt' 
                  ? 'A concentração no banco principal diminuiu face ao orçamento e a 2024, o que melhora a leitura de diversificação, mas continua material. Para a Gerência, este é um ponto a acompanhar, não por risco imediato de liquidez, mas por disciplina de tesouraria e gestão de concentração institucional.'
                  : 'Concentration in the main bank decreased vs budget and 2024, improving the diversification reading, but remains material. For Management, this is a point to monitor, not for immediate liquidity risk, but for treasury discipline and institutional concentration management.'}
              </p>
            </div>
          </div>

          {/* 7. Síntese à Gerência - Premium Gold Callout */}
          <div className="bg-gradient-to-r from-[#FBF8F0] to-[#F8F4E8] rounded-xl p-5 border border-[#B89A3C]/30">
            <h4 className="font-semibold text-sm text-[#8B7A2F] mb-3 flex items-center gap-2 uppercase tracking-wider">
              <Target className="w-4 h-4" />
              {language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, a página de <strong>Liquidez e Geração de Caixa</strong> confirma uma melhoria clara na <strong>qualidade da geração operacional</strong>. A empresa converteu EBITDA em caixa com maior eficácia, fechou com <strong>USD 79,0M de fluxo operacional</strong>, <strong>USD 45,0M de free cash flow</strong> e <strong>runway de 8 meses</strong>, sustentando investimento e compromissos financeiros sem perda de controlo sobre a posição de liquidez.</>
                  : <>In 2025, the <strong>Liquidity and Cash Generation</strong> page confirms a clear improvement in <strong>operating generation quality</strong>. The company converted EBITDA to cash more effectively, closed with <strong>USD 79.0M operating flow</strong>, <strong>USD 45.0M free cash flow</strong> and <strong>8-month runway</strong>, sustaining investment and financial commitments without losing control over liquidity position.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A principal leitura de gestão deve centrar-se em quatro pontos:' : 'The main management reading should focus on four points:'}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-2">
                <li>• {language === 'pt' ? 'a geração operacional foi forte e acima do plano;' : 'operating generation was strong and above plan;'}</li>
                <li>• {language === 'pt' ? 'o CAPEX e o financiamento absorveram uma parte material da caixa gerada;' : 'CAPEX and financing absorbed a material portion of generated cash;'}</li>
                <li>• {language === 'pt' ? 'o efeito cambial voltou a ter impacto relevante sobre a posição final;' : 'FX effect again had relevant impact on ending position;'}</li>
                <li>• {language === 'pt' ? 'a liquidez terminou o ano confortável, mas com concentração bancária ainda material no BFA.' : 'liquidity ended the year comfortable, but with still material banking concentration at BFA.'}</li>
              </ul>
              <div className="bg-white/60 rounded-lg p-3 border border-[#B89A3C]/20 mt-3">
                <p className="text-xs text-[#6B5A1F] font-medium">
                  <span className="text-[#B89A3C]">▸ </span>
                  {language === 'pt' 
                    ? 'Em síntese, 2025 não foi um problema de geração de caixa; foi sobretudo um ano de utilização intensiva de caixa gerada, com boa disciplina operacional, mas com pressão relevante de investimento, serviço financeiro e câmbio.'
                    : 'In summary, 2025 was not a cash generation problem; it was mainly a year of intensive use of generated cash, with good operational discipline, but with relevant pressure from investment, financial service and FX.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ============ DRILLDOWN NAVIGATION CARDS ============ */}
        {setActiveSection && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'pt' ? 'Análises Relacionadas' : 'Related Analysis'}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <ExecutiveDrilldownCard
                icon={BarChart3}
                title={language === 'pt' ? 'EBITDA' : 'EBITDA'}
                subtitle={language === 'pt' ? 'Geração operacional' : 'Operating generation'}
                navigateTo="ebitda-detail"
                setActiveSection={setActiveSection}
                color="blue"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'O EBITDA é a principal fonte de geração de caixa operacional.'
                    : 'EBITDA is the main source of operating cash generation.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={CreditCard}
                title={language === 'pt' ? 'Dívida' : 'Debt'}
                subtitle={language === 'pt' ? 'Serviço e estrutura' : 'Service and structure'}
                navigateTo="debt"
                setActiveSection={setActiveSection}
                color="rose"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise a estrutura de dívida e o impacto do serviço financeiro na liquidez.'
                    : 'Analyze debt structure and financial service impact on liquidity.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={Target}
                title={language === 'pt' ? 'CAPEX' : 'CAPEX'}
                subtitle={language === 'pt' ? 'Investimento realizado' : 'Investment made'}
                navigateTo="capex"
                setActiveSection={setActiveSection}
                color="amber"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Veja o detalhe dos investimentos que consumiram caixa no período.'
                    : 'See detail of investments that consumed cash in the period.'}
                </p>
              </ExecutiveDrilldownCard>
            </div>
          </div>
        )}
      </ExecutiveSummarySection>

      {/* Cash Flow Waterfall - Classic Style with Full Labels and Values */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-6">
          {language === 'pt' ? 'Movimentação de Caixa 2025' : 'Cash Movement 2025'}
        </h3>
        
        {/* Classic Waterfall Chart - McKinsey Style */}
        <div className="relative" style={{ height: '360px' }}>
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 h-64 w-10 flex flex-col justify-between text-right pr-2">
            <span className="text-xs text-slate-400">120M</span>
            <span className="text-xs text-slate-400">90M</span>
            <span className="text-xs text-slate-400">60M</span>
            <span className="text-xs text-slate-400">30M</span>
            <span className="text-xs text-slate-400">0M</span>
          </div>
          
          {/* Grid lines */}
          <div className="absolute left-12 right-4 top-0 h-64 flex flex-col justify-between pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="border-t border-dashed border-slate-200 w-full"></div>
            ))}
          </div>
          
          {/* Chart area */}
          <div className="absolute left-14 right-4 top-0 h-64 flex items-end justify-between gap-3">
            {(() => {
              // Use actualCashFlowData for 2025 (Budget + 5%)
              const chartData = actualCashFlowData || cashFlowData;
              const openingCash = chartData.openingCash;
              const operatingCF = chartData.operatingCashFlow;
              const investingCF = chartData.investingCashFlow;
              const financingCF = chartData.financingCashFlow;
              const fxEffect = chartData.fxEffect;
              const closingCash = chartData.closingCash;
              
              const afterOperating = openingCash + operatingCF;
              const afterInvesting = afterOperating + investingCF;
              const afterFinancing = afterInvesting + financingCF;
              
              const maxValue = 130;
              const chartHeight = 256;
              const scale = chartHeight / maxValue;
              
              const bars = [
                { label: language === 'pt' ? 'Caixa no Início do Período' : 'Opening Cash', value: openingCash, height: openingCash * scale, bottom: 0, type: 'total-dark' },
                { label: language === 'pt' ? 'Fluxo de Caixa Operacional' : 'Operating Cash Flow', value: operatingCF, height: operatingCF * scale, bottom: openingCash * scale, type: 'positive' },
                { label: language === 'pt' ? 'Fluxo de Caixa de Investimento' : 'Investing Cash Flow', value: investingCF, height: Math.abs(investingCF) * scale, bottom: afterInvesting * scale, type: 'negative' },
                { label: language === 'pt' ? 'Fluxo de Caixa de Financiamento' : 'Financing Cash Flow', value: financingCF, height: Math.abs(financingCF) * scale, bottom: afterFinancing * scale, type: 'negative' },
                { label: language === 'pt' ? 'Efeito das Diferenças de Câmbio' : 'FX Effect', value: fxEffect, height: Math.abs(fxEffect) * scale, bottom: closingCash * scale, type: 'negative' },
                { label: language === 'pt' ? 'Caixa no Final do Período' : 'Closing Cash', value: closingCash, height: closingCash * scale, bottom: 0, type: 'total-dark' }
              ];
              
              return bars.map((bar, idx) => (
                <div key={idx} className="flex-1 relative h-full">
                  {/* Value label above bar */}
                  <div 
                    className="absolute left-1/2 -translate-x-1/2 text-center"
                    style={{ bottom: `${bar.bottom + bar.height + 4}px` }}
                  >
                    <span className={`text-xs font-semibold ${bar.value >= 0 ? 'text-slate-700' : 'text-red-600'}`}>
                      {bar.value >= 0 ? '' : ''}{formatM(bar.value)}M
                    </span>
                  </div>
                  <div 
                    className={`absolute left-1/2 -translate-x-1/2 w-10 ${
                      bar.type === 'total-dark' ? 'bg-[#0A192F]' : 
                      bar.type === 'total-light' ? 'bg-slate-400' : 
                      bar.type === 'positive' ? 'bg-emerald-500' : 'bg-red-600'
                    }`}
                    style={{ bottom: `${bar.bottom}px`, height: `${bar.height}px` }}
                  />
                </div>
              ));
            })()}
          </div>
          
          {/* Baseline */}
          <div className="absolute left-12 right-4 top-64 border-t-2 border-slate-300"></div>
          
          {/* X-axis labels - Full names */}
          <div className="absolute left-14 right-4 top-[268px] flex justify-between gap-3">
            {[
              language === 'pt' ? 'Caixa Inicial' : 'Opening Cash',
              language === 'pt' ? 'Fluxo Caixa Operacional' : 'Operating CF',
              language === 'pt' ? 'Fluxo Caixa de Investimento' : 'Investing CF',
              language === 'pt' ? 'Fluxo Caixa de Financiamento' : 'Financing CF',
              language === 'pt' ? 'Efeito Câmbio' : 'FX Effect',
              language === 'pt' ? 'Caixa Final' : 'Closing Cash'
            ].map((label, idx) => (
              <div key={idx} className="flex-1 text-center">
                <span className="text-[9px] text-slate-500 leading-tight block">{label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-[#0A192F]"></div>
            <span className="text-xs text-slate-600">{language === 'pt' ? 'Saldo Total' : 'Total'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-500"></div>
            <span className="text-xs text-slate-600">{language === 'pt' ? 'Entrada' : 'Inflow'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-600"></div>
            <span className="text-xs text-slate-600">{language === 'pt' ? 'Saída' : 'Outflow'}</span>
          </div>
        </div>
      </div>

      {/* Deposit Type KPIs */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                {language === 'pt' ? 'Depósitos à Ordem' : 'Demand Deposits'}
              </p>
              <p className="font-serif text-2xl font-semibold text-primary">
                38.5<span className="text-sm text-slate-400 ml-1">M USD</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">69% {language === 'pt' ? 'da caixa total' : 'of total cash'}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-emerald-600 text-sm font-semibold">
                <TrendingUp className="w-4 h-4" />
                +12% YoY
              </div>
              <p className="text-xs text-slate-400 mt-1">2024: 34.4M</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                {language === 'pt' ? 'Depósitos a Prazo' : 'Term Deposits'}
              </p>
              <p className="font-serif text-2xl font-semibold text-primary">
                17.5<span className="text-sm text-slate-400 ml-1">M USD</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">31% {language === 'pt' ? 'da caixa total' : 'of total cash'}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-emerald-600 text-sm font-semibold">
                <TrendingUp className="w-4 h-4" />
                +8% YoY
              </div>
              <p className="text-xs text-slate-400 mt-1">2024: 16.3M</p>
            </div>
          </div>
        </div>
      </div>

      {/* Two Charts Side by Side - Enhanced with Deposit Type Split */}
      <div className="grid grid-cols-2 gap-6">
        {/* Chart 1 - Cash by Currency with Deposit Split */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Caixa por Moeda' : 'Cash by Currency'}
          </h3>
          <p className="text-[10px] text-slate-400 mb-5">
            {language === 'pt' ? 'Inclui split entre depósito à ordem e depósito a prazo' : 'Includes split between demand and term deposits'}
          </p>
          <div className="space-y-4">
            {[
              { currency: 'USD', currentDemand: 30.0, currentTerm: 12.5, priorDemand: 27.0, priorTerm: 11.2 },
              { currency: 'AOA', currentDemand: 5.5, currentTerm: 2.7, priorDemand: 5.0, priorTerm: 2.8 },
              { currency: 'EUR', currentDemand: 2.0, currentTerm: 1.8, priorDemand: 1.8, priorTerm: 1.7 },
              { currency: 'ZAR', currentDemand: 1.0, currentTerm: 0.5, priorDemand: 0.6, priorTerm: 0.6 }
            ].map((item, idx) => {
              const currentTotal = item.currentDemand + item.currentTerm;
              const priorTotal = item.priorDemand + item.priorTerm;
              const maxVal = Math.max(currentTotal, priorTotal);
              const scale = 100 / maxVal;
              const currentDemandPct = (item.currentDemand / currentTotal) * 100;
              const priorDemandPct = (item.priorDemand / priorTotal) * 100;
              
              return (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700 w-12">{item.currency}</span>
                    <div className="flex-1 ml-4 space-y-1">
                      {/* 2025 Bar - Stacked */}
                      <div className="flex items-center gap-2">
                        <div className="w-10 text-[10px] text-slate-500 text-right">{year}</div>
                        <div className="flex-1 bg-slate-100 rounded-full h-5 relative overflow-hidden">
                          {/* Demand Deposit (full color) */}
                          <div 
                            className="absolute left-0 top-0 h-full bg-[#0A192F] rounded-l-full"
                            style={{ width: `${item.currentDemand * scale}%` }}
                          ></div>
                          {/* Term Deposit (lighter shade) */}
                          <div 
                            className="absolute top-0 h-full bg-[#1E3A5F] rounded-r-full"
                            style={{ left: `${item.currentDemand * scale}%`, width: `${item.currentTerm * scale}%` }}
                          ></div>
                          {/* Total Value */}
                          <div 
                            className="absolute right-0 top-0 h-full flex items-center justify-end pr-2"
                            style={{ width: `${currentTotal * scale}%` }}
                          >
                            <span className="text-[10px] font-semibold text-white">{currentTotal.toFixed(1)}M</span>
                          </div>
                        </div>
                      </div>
                      {/* Prior Year Bar - Stacked */}
                      <div className="flex items-center gap-2">
                        <div className="w-10 text-[10px] text-slate-500 text-right">{year - 1}</div>
                        <div className="flex-1 bg-slate-100 rounded-full h-5 relative overflow-hidden">
                          {/* Demand Deposit (full color) */}
                          <div 
                            className="absolute left-0 top-0 h-full bg-slate-500 rounded-l-full"
                            style={{ width: `${item.priorDemand * scale}%` }}
                          ></div>
                          {/* Term Deposit (lighter shade) */}
                          <div 
                            className="absolute top-0 h-full bg-slate-300 rounded-r-full"
                            style={{ left: `${item.priorDemand * scale}%`, width: `${item.priorTerm * scale}%` }}
                          ></div>
                          {/* Total Value */}
                          <div 
                            className="absolute right-0 top-0 h-full flex items-center justify-end pr-2"
                            style={{ width: `${priorTotal * scale}%` }}
                          >
                            <span className="text-[10px] font-semibold text-white">{priorTotal.toFixed(1)}M</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#0A192F]"></div>
                <span className="text-[10px] text-slate-600">{year} {language === 'pt' ? 'À Ordem' : 'Demand'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#1E3A5F]"></div>
                <span className="text-[10px] text-slate-600">{year} {language === 'pt' ? 'A Prazo' : 'Term'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-slate-500"></div>
                <span className="text-[10px] text-slate-600">{year - 1} {language === 'pt' ? 'À Ordem' : 'Demand'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-slate-300"></div>
                <span className="text-[10px] text-slate-600">{year - 1} {language === 'pt' ? 'A Prazo' : 'Term'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Chart 2 - Bank Concentration with Deposit Split */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Concentração Bancária' : 'Bank Concentration'}
          </h3>
          <p className="text-[10px] text-slate-400 mb-4">
            {language === 'pt' ? 'Posição de caixa com distinção entre liquidez imediata e aplicações a prazo' : 'Cash position with distinction between immediate liquidity and term applications'}
          </p>
          <div className="space-y-3">
            {[
              { bank: 'BFA', currentDemand: 20.0, currentTerm: 9.1, priorDemand: 19.0, priorTerm: 8.5, pct: 52 },
              { bank: 'BAI', currentDemand: 8.5, currentTerm: 3.8, priorDemand: 8.2, priorTerm: 3.6, pct: 22 },
              { bank: 'BIC', currentDemand: 5.8, currentTerm: 2.6, priorDemand: 4.5, priorTerm: 2.7, pct: 15 },
              { bank: 'Standard Bank', currentDemand: 4.2, currentTerm: 2.0, priorDemand: 4.0, priorTerm: 1.8, pct: 11 }
            ].sort((a, b) => (b.currentDemand + b.currentTerm) - (a.currentDemand + a.currentTerm)).map((item, idx) => {
              const currentTotal = item.currentDemand + item.currentTerm;
              const priorTotal = item.priorDemand + item.priorTerm;
              const maxVal = 35;
              const scale = 100 / maxVal;
              
              return (
                <div key={idx} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700 w-28">{item.bank}</span>
                    <span className="text-xs text-slate-500">{item.pct}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 space-y-1">
                      {/* Current Year Bar - Stacked with values */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded h-7 relative overflow-hidden">
                          {/* Demand Deposit with value */}
                          <div 
                            className="absolute left-0 top-0 h-full bg-[#0A192F] rounded-l flex items-center justify-center"
                            style={{ width: `${item.currentDemand * scale}%` }}
                          >
                            <span className="text-[9px] font-semibold text-white">{item.currentDemand.toFixed(1)}</span>
                          </div>
                          {/* Term Deposit with value */}
                          <div 
                            className="absolute top-0 h-full bg-[#1E3A5F] rounded-r flex items-center justify-center"
                            style={{ left: `${item.currentDemand * scale}%`, width: `${item.currentTerm * scale}%` }}
                          >
                            <span className="text-[9px] font-semibold text-white">{item.currentTerm.toFixed(1)}</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-600 w-14 text-right font-medium">{currentTotal.toFixed(1)}M</span>
                        <span className="text-[10px] text-slate-500 w-8">{year}</span>
                      </div>
                      {/* Prior Year Bar - Stacked with values */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded h-7 relative overflow-hidden">
                          {/* Demand Deposit with value */}
                          <div 
                            className="absolute left-0 top-0 h-full bg-slate-500 rounded-l flex items-center justify-center"
                            style={{ width: `${item.priorDemand * scale}%` }}
                          >
                            <span className="text-[9px] font-semibold text-white">{item.priorDemand.toFixed(1)}</span>
                          </div>
                          {/* Term Deposit with value */}
                          <div 
                            className="absolute top-0 h-full bg-slate-300 rounded-r flex items-center justify-center"
                            style={{ left: `${item.priorDemand * scale}%`, width: `${item.priorTerm * scale}%` }}
                          >
                            <span className="text-[9px] font-semibold text-slate-700">{item.priorTerm.toFixed(1)}</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-500 w-14 text-right">{priorTotal.toFixed(1)}M</span>
                        <span className="text-[10px] text-slate-500 w-8">{year - 1}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#0A192F]"></div>
                <span className="text-[10px] text-slate-600">{year} {language === 'pt' ? 'À Ordem' : 'Demand'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#1E3A5F]"></div>
                <span className="text-[10px] text-slate-600">{year} {language === 'pt' ? 'A Prazo' : 'Term'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-slate-500"></div>
                <span className="text-[10px] text-slate-600">{year - 1} {language === 'pt' ? 'À Ordem' : 'Demand'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-slate-300"></div>
                <span className="text-[10px] text-slate-600">{year - 1} {language === 'pt' ? 'A Prazo' : 'Term'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cash Flow Statement Table with Sticky Header */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Demonstração dos Fluxos de Caixa' : 'Cash Flow Statement'}
          </h3>
        </div>
        {/* Scrollable Container with Sticky Header */}
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-[#0A192F]">
              <tr className="text-white">
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider w-[28%]">
                  {language === 'pt' ? 'Descrição' : 'Description'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider w-[10%]">
                  {year === 2026 ? '1T2026' : year}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider w-[10%]">
                  {year === 2026 
                    ? (language === 'pt' ? 'Orç. 1T2026' : 'Budget Q1 2026')
                    : (<>{language === 'pt' ? 'Orç.' : 'Budget'}<br/>{year}</>)
                  }
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-amber-300 w-[10%]">
                  {language === 'pt' ? 'Var. Abs.' : 'Var. Abs.'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-amber-300 w-[10%]">
                  {language === 'pt' ? 'Var. %' : 'Var. %'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 w-[10%]">
                  {year === 2026 ? '1T2025' : (year - 1)}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 w-[12%]">
                  {language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}
                </th>
              </tr>
            </thead>
            <tbody>
              {cashFlowRows.map((row, idx) => {
                // Use actualCashFlowData (Budget + 5% for 2025) for the "Real" column
                const currentVal = actualCashFlowData ? actualCashFlowData[row.key] : cashFlowData[row.key];
                // Use REAL previous year data for the Prior Year column
                const prevVal = prevYearRealData ? prevYearRealData[row.key] : (prevCashFlowData ? prevCashFlowData[row.key] : 0);
                // Use REAL budget data (unchanged) for the Budget column
                const budgetVal = budgetCashFlowData ? budgetCashFlowData[row.key] : 
                                  (row.key === 'operatingCashFlow' ? currentVal * 0.95 :
                                   row.key === 'investingCashFlow' ? currentVal * 1.1 :
                                   row.key === 'financingCashFlow' ? currentVal * 0.9 :
                                   row.key === 'closingCash' ? budgetClosingCash :
                                   currentVal * 0.98);
                const varAbs = (currentVal || 0) - (budgetVal || 0);
                const varPct = budgetVal !== 0 && budgetVal !== undefined ? ((currentVal - budgetVal) / Math.abs(budgetVal) * 100).toFixed(1) : '0';
                const varYoY = prevVal !== 0 && prevVal !== undefined ? ((currentVal - prevVal) / Math.abs(prevVal) * 100).toFixed(1) : '0';
                
                if (row.type === 'header') {
                  return (
                    <tr key={idx} className="bg-slate-100">
                      <td colSpan={7} className="py-3 px-4 font-semibold text-slate-700">
                        {row.label[language]}
                      </td>
                    </tr>
                  );
                }
                
                return (
                  <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50 ${
                    row.type === 'total' || row.type === 'grandtotal' ? 'bg-amber-50/30 font-semibold' :
                    row.type === 'subtotal' ? 'bg-slate-50 font-medium' : ''
                  }`}>
                    <td className={`py-2.5 px-4 w-[28%] ${row.type === 'row' ? 'pl-8' : ''}`}>
                      {row.label[language]}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums font-medium w-[10%] ${
                      row.type === 'total' || row.type === 'grandtotal' ? 'text-primary' : ''
                    }`}>
                      {formatM(currentVal)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-500 w-[10%]">
                      {formatM(budgetVal)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums w-[10%]">
                      <span className={varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {varAbs >= 0 ? '+' : ''}{formatM(varAbs)}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums w-[10%]">
                      <span className={parseFloat(varPct) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {parseFloat(varPct) >= 0 ? '+' : ''}{varPct}%
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-400 w-[10%]">
                      {formatM(prevVal)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums w-[12%]">
                      <span className={parseFloat(varYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {parseFloat(varYoY) >= 0 ? '+' : ''}{varYoY}%
                      </span>
                    </td>
                  </tr>
                );
              })}
              </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LiquidityCashFlow;
